import { api } from '~shared/api/origin';
import { transformImageUrl } from '~utils/messages';

import { dialogsApi, messagesAdapter, messagesSelector } from '../dialogs';
import type {
	GetMediaDetailsApiResponse,
	GetMediaLinksApiResponse,
	GetMediaPackApiResponse,
} from './types';

export const {
	useGetImageSrcQuery,
	useGetMediaPackQuery,
	useGetMediaDetailsQuery,
	useGetMediaLinksQuery,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getImageSrc: build.query<string, { url: string }>({
			query: ({ url }) => ({
				url,
				responseHandler: async (response) => {
					try {
						const blob = await response.blob();
						return URL.createObjectURL(blob);
					} catch {
						return '';
					}
				},
			}),
		}),
		getMediaPack: build.query<
			GetMediaPackApiResponse['linkedMedia'],
			{ userId: string; botId: string; messageId: string | number }
		>({
			query: ({ userId, botId, messageId }) => ({
				url: `v2/ai/dialogs/messages/${userId}/${botId}/${messageId}/packcontent`,
			}),
			transformResponse: (
				response: GetMediaPackApiResponse,
				_,
				arg
			): GetMediaPackApiResponse['linkedMedia'] => {
				const { userId, messageId, botId } = arg;

				return response
					? response.linkedMedia
							.map((it) => {
								return {
									...it,
									reference: transformImageUrl({
										url: it.reference,
										userId,
										messageId: String(messageId),
										botId,
									}),
									preview: transformImageUrl({
										url: it.reference.replace('file', 'preview'),
										userId,
										messageId: String(messageId),
										botId,
									}),
								};
							})
							// sort -> x, xx, xxx
							.sort((a, b) => a.rating?.length - b.rating?.length)
					: [];
			},
		}),
		getMediaDetails: build.query<
			GetMediaDetailsApiResponse,
			{ userId: string; mediaId: string }
		>({
			query: ({ userId, mediaId }) => ({
				url: `ai/user/${userId}/video/v2/${mediaId}`,
				method: 'GET',
			}),
		}),
		getMediaLinks: build.query<
			GetMediaLinksApiResponse,
			{
				userId: string;
				mediaId: string;
				messageId: number;
				recipientId: string;
			}
		>({
			query: ({ userId, mediaId }) => ({
				url: `dialogs/media/photos/links/${userId}/${mediaId}`,
				method: 'GET',
			}),
			async onQueryStarted(
				{ userId, messageId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						dialogsApi.util.updateQueryData(
							'getMessages',
							{ id: userId, recipientId, omit: 0 },
							(draft) => {
								const message = messagesSelector
									.selectAll(draft.messages)
									.find(({ id }) => id === messageId);
								if (!message) return;

								messagesAdapter.updateOne(draft.messages, {
									id: message.tag,
									changes: {
										meta: {
											...message.meta,
											tariffication: {
												...message.meta?.tariffication,
												final: message.meta?.tariffication.final ?? true,
											},
											mediaLinks: res.links,
										},
									},
								});
							}
						)
					);
				} catch (e) {}
			},
		}),
	}),
});
